import {
  Directive,
  HostListener,
  ElementRef,
  Renderer2,
  inject,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import {
  formatDateMask,
  MONTH_YEAR_MASK_BOUNDARIES,
  MONTH_YEAR_MASK_MAX_DIGITS,
} from './date-mask.util';

/**
 * Input mask/restriction directive for month-year fields.
 *
 * - Auto-inserts the `.` separator while typing (MM.YYYY), so users only need
 *   the digit keys — no keyboard switch for the dot on tablets.
 * - Only allows digits and dots
 * - Limits input to 7 characters (MM.YYYY)
 * - Backspace/Delete over the auto-inserted dot removes the adjacent digit
 * - Validates format on blur
 *
 * Usage:
 * ```html
 * <input matInput [matDatepicker]="picker" appMonthYearInputRestriction />
 * ```
 */
@Directive({
  selector: 'input[matInput][matDatepicker][appMonthYearInputRestriction]',
  standalone: true,
})
export class MonthYearInputRestrictionDirective {
  private el = inject(ElementRef<HTMLInputElement>);
  private renderer = inject(Renderer2);
  private ngControl = inject(NgControl, { optional: true });
  private lastInputValue = '';
  private isReformatting = false;

  private readonly boundaries = MONTH_YEAR_MASK_BOUNDARIES;
  private readonly maxDigits = MONTH_YEAR_MASK_MAX_DIGITS;

  @HostListener('input')
  onInput(): void {
    const input = this.el.nativeElement;
    if (this.isReformatting) return;

    if (input.readOnly || input.disabled) {
      this.lastInputValue = input.value || '';
      return;
    }

    const raw = input.value || '';
    const caret = input.selectionStart ?? raw.length;
    this.applyMask(input, raw, caret);
  }

  @HostListener('blur')
  onBlur(): void {
    if (!this.ngControl?.control) return;

    const value = this.lastInputValue.trim();

    if (value && !this.ngControl.control.value) {
      const errors = this.ngControl.control.errors || {};
      this.ngControl.control.setErrors({
        ...errors,
        matDatepickerParse: { text: value },
      });
      this.ngControl.control.markAsTouched();
    }
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const input = this.el.nativeElement;
    const key = event.key;

    // Backspace/Delete over the auto-inserted separator: remove the adjacent
    // DIGIT instead so the caret does not get stuck on the dot.
    if (
      (key === 'Backspace' || key === 'Delete') &&
      !input.readOnly &&
      !input.disabled &&
      input.selectionStart === input.selectionEnd
    ) {
      const pos = input.selectionStart ?? 0;
      const value = input.value || '';
      if (key === 'Backspace' && pos >= 2 && value[pos - 1] === '.') {
        event.preventDefault();
        this.applyMask(
          input,
          value.slice(0, pos - 2) + value.slice(pos - 1),
          pos - 2
        );
        return;
      }
      if (key === 'Delete' && value[pos] === '.') {
        event.preventDefault();
        this.applyMask(input, value.slice(0, pos) + value.slice(pos + 2), pos);
        return;
      }
    }

    if (
      key === 'Backspace' ||
      key === 'Delete' ||
      key === 'Tab' ||
      key === 'Escape' ||
      key === 'Enter' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'Home' ||
      key === 'End' ||
      event.metaKey ||
      event.ctrlKey
    ) {
      return;
    }

    if (!/^[0-9.]$/.test(key)) {
      event.preventDefault();
      return;
    }

    const value = input.value || '';
    const hasSelection =
      input.selectionStart !== null &&
      input.selectionEnd !== null &&
      input.selectionStart !== input.selectionEnd;

    if (value.length >= 7 && !hasSelection) {
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    const input = this.el.nativeElement;
    if (input.readOnly || input.disabled) return;

    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const currentValue = input.value || '';
    const start = input.selectionStart ?? currentValue.length;
    const end = input.selectionEnd ?? currentValue.length;
    const merged =
      currentValue.slice(0, start) + pastedText + currentValue.slice(end);
    this.applyMask(input, merged, start + pastedText.length);
  }

  /**
   * Reformat `raw` into the month-year mask, write it back to the input and let
   * Angular Material re-parse it by re-dispatching an `input` event.
   */
  private applyMask(input: HTMLInputElement, raw: string, caret: number): void {
    const { value: formatted, caret: newCaret } = formatDateMask(
      raw,
      caret,
      this.boundaries,
      this.maxDigits
    );
    this.lastInputValue = formatted;

    if (formatted === input.value) return;

    this.isReformatting = true;
    this.renderer.setProperty(input, 'value', formatted);
    input.setSelectionRange(newCaret, newCaret);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    this.isReformatting = false;
  }
}
